import { Box, MenuItem, Select, SelectChangeEvent, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useMemo } from 'react';
import { NavLink, useHistory } from 'react-router-dom';

import { ChevronDown } from 'lucide-react';
import NotificationBadge, { BadgeType } from './notification-badge';

const Root = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(2),
  flexDirection: 'column',

  [theme.breakpoints.up('md')]: {
    marginBottom: theme.spacing(6),
  },
}));

const NavContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  flex: 1,
  alignItems: 'stretch',
  borderBottom: `1px solid ${theme.palette.lightGray.light}`,
  color: theme.palette.darkBlue.main,

  [theme.breakpoints.up('md')]: {
    alignItems: 'center',
    flexDirection: 'row',
  },
}));

const TabsSubContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  flexGrow: 1,
  margin: theme.spacing(0, 0),
}));

const MobileSelectBox = styled(Box)(({ theme }) => ({
  width: '60%',
  background: '#ffffff16',
  borderTopLeftRadius: theme.spacing(4),
  borderTopRightRadius: theme.spacing(4),
  color: theme.palette.common.white,
  marginBottom: -theme.spacing(12),
}));

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',

  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}));

const Intro = styled('div')(({ theme }) => ({
  fontSize: 18,
  color: theme.palette.gray.dark,
  padding: theme.spacing(0, 0, 5),
  maxWidth: 980,
  marginBottom: theme.spacing(8),
}));

const Utils = styled('div')(({ theme }) => ({}));

type TabItem = {
  label: React.ReactNode;
  to: string;
  hasAccess: boolean | undefined;
  icon?: React.ReactNode;
  external?: boolean;
  notificationBadge?: number;
};

type TabsHeaderProps = {
  tabs: TabItem[];
  icon?: React.ReactNode;
  intro?: React.ReactNode;
  actions?: React.ReactNode;
  utils?: React.ReactNode;
  showIcon?: boolean;
};

const TabsHeader: React.FC<TabsHeaderProps> = ({ tabs, showIcon = true, intro, actions, utils, icon }) => {
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const currentTabValue = useMemo(() => {
    let currentPathname = history.location.pathname;
    // Normalize current pathname (add trailing slash if missing, unless it's root)
    if (currentPathname !== '/' && !currentPathname.endsWith('/')) {
      currentPathname += '/';
    }

    let bestMatchValue = '';
    let longestMatchLength = 0;

    for (const tab of tabs) {
      let tabToNormalized = tab.to;
      // Normalize tab.to (add trailing slash if missing, unless it's root)
      if (tabToNormalized !== '/' && !tabToNormalized.endsWith('/')) {
        tabToNormalized += '/';
      }

      // Prioritize exact match first
      if (currentPathname === tabToNormalized) return tab.to;

      // Check for prefix match (for parent/sub-tab highlighting like /sites/active highlighting /sites/)
      if (currentPathname.startsWith(tabToNormalized) && tabToNormalized.length > longestMatchLength) {
        // If this is a longer prefix match than previous ones, update bestMatchValue
        // This ensures e.g., /a/b/ is preferred over /a/ if the URL is /a/b/c/
        longestMatchLength = tabToNormalized.length;
        bestMatchValue = tab.to;
      }
    }

    // If an exact match was found, it would have been returned already.
    // Otherwise, return the longest prefix match found.
    // If no match at all, it will return the initial empty string.
    return bestMatchValue;
  }, [history.location.pathname, tabs]);

  const handleNavigation = (newValue: string) => {
    const tab = tabs.find((t) => t.to === newValue);
    if (tab?.external) {
      const newWindow = window.open(newValue, '_blank', 'noopener,noreferrer');
      if (newWindow) newWindow.opener = null;
    } else {
      history.push(newValue);
    }
  };

  const handleTabsChange = (event: React.SyntheticEvent, newValue: string) => {
    handleNavigation(newValue);
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    handleNavigation(event.target.value);
  };

  const renderMobile = () => (
    <Box display={'flex'} flexDirection={'row'} justifyContent={'space-around'} alignItems={'center'}>
      <Box>{showIcon && icon}</Box>
      <TabsSubContainer>
        <MobileSelectBox>
          <Select
            fullWidth
            label={'Nav'}
            variant="outlined"
            value={currentTabValue}
            IconComponent={(props) => (
              <span {...props}>
                <ChevronDown size={18} color={theme.palette.common.white} />
              </span>
            )}
            sx={{
              '&.MuiInputBase-root': { color: 'white !important' },
              '& .MuiSelect-icon': { right: 15 },
            }}
            onChange={handleSelectChange}>
            {tabs.map(
              (tab) =>
                tab.hasAccess && (
                  <MenuItem key={tab.to} value={tab.to}>
                    {tab.label}
                  </MenuItem>
                )
            )}
          </Select>
        </MobileSelectBox>
      </TabsSubContainer>
    </Box>
  );

  const renderDesktop = () => (
    <>
      <Box>{showIcon && icon}</Box>
      <TabsSubContainer>
        <Tabs
          value={currentTabValue}
          onChange={handleTabsChange}
          aria-label="navigation tabs"
          slotProps={{
            indicator: {
              style: { display: 'none' },
            },
          }}
          sx={{
            '& .MuiTabs-flexContainer': {
              justifyContent: 'flex-end',
            },
          }}>
          {tabs.map(
            (tab) =>
              tab.hasAccess && (
                <Tab
                  key={tab.to}
                  sx={{
                    overflow: !!tab.notificationBadge ? 'visible' : 'hidden',
                  }}
                  label={
                    !!tab.notificationBadge ? (
                      <NotificationBadge color={BadgeType.WARNING} count={tab.notificationBadge}>
                        <>
                          {tab.icon} {tab.label}
                        </>
                      </NotificationBadge>
                    ) : (
                      <>
                        {tab.icon} {tab.label}
                      </>
                    )
                  }
                  value={tab.to}
                  component={tab.external ? 'a' : NavLink}
                  {...(tab.external ? { href: tab.to, target: '_blank', rel: 'noopener noreferrer' } : { to: tab.to })}
                />
              )
          )}
        </Tabs>
      </TabsSubContainer>
    </>
  );

  return (
    <Root>
      {intro && <Intro>{intro}</Intro>}
      <NavContainer>
        {isMobile ? renderMobile() : renderDesktop()}
        {actions && <Actions>{actions}</Actions>}
      </NavContainer>
      {utils && <Utils>{utils}</Utils>}
    </Root>
  );
};

export default React.memo(TabsHeader);
